import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Package,
  Folder,
  BarChart3,
  ListChecks,
  Ruler,
  Tag,
  Shield,
  UserCog,
  Bell,
  FileEdit,
  Image,
  X,
  Menu,
  LogOut,
} from "lucide-react";

import { onAdminDetail } from "../../redux/homeAction";

const Sidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [menuItems, setMenuItems] = useState([]);
  const [isOpen, setIsOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const getAdminDetail = useSelector((state) => state.home.getAdminDetail);

  useEffect(() => {
    if (localStorage.getItem("adminToken")) {
      dispatch(onAdminDetail(setLoading));
    } else {
      navigate("/login");
    }
  }, []);

  const allMenuItems = [
    {
      name: "Dashboard",
      path: "/",
      permission: "view_dashboard",
      icon: LayoutDashboard,
    },
    {
      name: "Manage Users",
      path: "/users",
      permission: "manage_users",
      icon: Users,
    },
    {
      name: "Manage Orders",
      path: "/orders",
      permission: "manage_orders",
      icon: ShoppingCart,
    },
    {
      name: "Products",
      path: "/products",
      permission: "manage_products",
      icon: Package,
    },
    {
      name: "Catalogs",
      path: "/catalogs",
      permission: "catalogs_products",
      icon: Folder,
    },
    {
      name: "Reports",
      path: "/reports",
      permission: "view_reports",
      icon: BarChart3,
    },
    {
      name: "Refund Page",
      path: "/refund-page",
      permission: "refund_page",
      icon: BarChart3,
    },
    {
      name: "Categories",
      path: "/categories",
      permission: "categories",
      icon: ListChecks,
    },
    {
      name: "Size Guide",
      path: "/sizeguide",
      permission: "sizeguide",
      icon: Ruler,
    },
    {
      name: "Offers",
      path: "/offers",
      permission: "offers",
      icon: Tag,
    },
    {
      name: "Role Management",
      path: "/role-management",
      permission: "role_management",
      icon: Shield,
    },
    {
      name: "Admin Management",
      path: "/admin-management",
      permission: "admin_management",
      icon: UserCog,
    },
    {
      name: "Notification",
      path: "/notification",
      permission: "notification",
      icon: Bell,
    },
    {
      name: "Page Editor",
      path: "/pages-editor",
      permission: "page_editor",
      icon: FileEdit,
    },
    {
      name: "Banner",
      path: "/banner",
      permission: "banner",
      icon: Image,
    },
  ];

  useEffect(() => {
    if (getAdminDetail) {
      let permissions = [];

      if (Array.isArray(getAdminDetail.role)) {
        permissions = getAdminDetail.role.flatMap((r) => r.permissions || []);
      } else if (getAdminDetail.role) {
        permissions = getAdminDetail.role.permissions || [];
      }

      setMenuItems(
        allMenuItems.filter((item) => permissions.includes(item.permission)),
      );
    }
  }, [getAdminDetail]);

  const logoutHandler = () => {
    localStorage.removeItem("adminToken");
    window.location.reload();
  };

  const SidebarContent = () => (
    <div className="h-full flex flex-col bg-white border-r shadow-sm">
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-3 border-b">
        <img
          src={`/images/logo/logo.png`}
          className={`transition-all duration-300 ${isOpen ? "h-10" : "h-8"}`}
          alt="logo"
        />

        <button
          className="hidden md:flex text-gray-600"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Menu */}
      <nav className="flex-1 p-2 space-y-1">
        {menuItems.map((item, i) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={i}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2 rounded-lg transition-all
                ${
                  isActive
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : "text-gray-600 hover:bg-gray-100"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {/* Active Indicator */}
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute left-0 top-0 h-full w-1 bg-blue-500 rounded-r"
                    />
                  )}

                  <Icon size={18} />

                  {isOpen && <span className="text-sm">{item.name}</span>}

                  {/* Tooltip when collapsed */}
                  {!isOpen && (
                    <span className="absolute left-14 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100">
                      {item.name}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t">
        <button
          onClick={logoutHandler}
          className="flex items-center justify-center gap-2 w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg transition"
        >
          <LogOut size={18} />
          {isOpen && "Logout"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Top Bar */}
      <div className="md:hidden p-3 bg-white shadow ">
        <button onClick={() => setMobileOpen(true)}>
          <Menu />
        </button>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`fixed z-50 top-0 left-0 h-full w-64 md:hidden transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarContent />
      </div>

      {/* Desktop Sidebar */}
      <div
        className={`hidden md:flex h-screen transition-all duration-300  ${
          isOpen ? "w-70" : "w-20"
        }`}
      >
        <SidebarContent />
      </div>
    </>
  );
};

export default Sidebar;
